import { cloneDeep, find, merge, reduce } from 'lodash-es'
import { denormalize, normalize } from 'normalizr'

import { BudgetAPI } from 'src/api'

import Category from './category'
import Product from './product'
import CategorySchema from './schema/category'
import Subproduct from './subproduct'

const subModules = [Product, Category, Subproduct]

const budgetModule = {
  namespaced: true,
  state: {
    rtb: {},
    loading: false,
    budgetType: 'targetBudget',
    campaigns: {},
    budgetsList: [],
    copyDirections: [],
    totalLoading: false,
  },
  mutations: {
    CLEAR_STATE(state) {
      state.rtb = {}
      state.campaigns = {}
      state.productsList = null
      state.products = {}
      state.categoriesList = []
      state.categories = {}
      state.categoryOrder = []
      state.subproducts = {}
      state.budgetsList = []
      state.copyDirections = []
      state.totalLoading = false
    },
    SET_TOTAL_LOADING(state, payload) {
      state.totalLoading = payload
    },
    SET_BUDGETS_LIST(state, payload) {
      state.budgetsList = payload
    },
    SET_COPY_DIRECTIONS(state, payload) {
      state.copyDirections = payload
    },
    SET_BUDGET(state, payload) {
      state.rtb = {
        ...state.rtb,
        ...payload,
      }
    },
    SET_STATS(state, payload) {
      state.rtb = {
        ...state.rtb,
        stats: {
          ...state.rtb.stats,
          ...payload,
        },
      }
    },
    SET_LOADING(state, payload) {
      state.loading = payload
    },
    SET_BUDGET_TYPE(state, payload) {
      state.budgetType = payload
    },
    SET_CAMPAIGNS(state, payload) {
      state.campaigns = payload
    },
  },
  actions: {
    async getCopyDirections({ commit }) {
      const copyDirections = await BudgetAPI.getCopyDirections()
      commit('SET_COPY_DIRECTIONS', copyDirections)
    },
    async getBudgetsList({ commit }) {
      const budgetsList = await BudgetAPI.getList()
      commit('SET_BUDGETS_LIST', budgetsList)
    },
    normalizeCategories({ commit }, categories) {
      const { result, entities } = normalize(categories, [CategorySchema])
      commit('SET_CATEGORIES', {
        result,
        entities: entities.categories,
      })
      commit('SET_PRODUCTS', entities.products)
      commit('SET_SUBPRODUCTS', entities.subproducts)
    },

    getRtb({ commit, dispatch }) {
      commit('SET_LOADING', true)

      return BudgetAPI.getRtb().then((data) => {
        dispatch('normalizeCategories', data.categories)
        commit('SET_BUDGET', data)
        commit('SET_LOADING', false)
      })
    },

    getTotal({ commit, dispatch }, disableLoading = false) {
      if (!disableLoading) {
        commit('SET_LOADING', true)
      }
      return BudgetAPI.getTotal().then((data) => {
        dispatch('normalizeCategories', data.categories)
        commit('SET_BUDGET', data)
        commit('SET_LOADING', false)
      })
    },
    async getCampaigns({ commit }, disableLoading = false) {
      if (!disableLoading) {
        commit('SET_LOADING', true)
      }

      const data = await BudgetAPI.getCampaigns()

      if (!data.campaigns.length) {
        const budgets = {}
        for (const dealershipId in { ...data.dealerships }) {
          if (Object.hasOwnProperty.call(data.dealerships, dealershipId)) {
            budgets[dealershipId] = {
              '': {
                planned: null,
                actual: null,
              },
              'No campaign': {
                planned: null,
                actual: null,
              },
            }
          }
        }
        data.budgets = budgets
        data.campaigns = ['', '']
      }

      commit('SET_CAMPAIGNS', data)
      commit('SET_LOADING', false)
    },
    copyPlannedToActual({ commit }) {
      commit('SET_LOADING', true)
      return BudgetAPI.copyPlannedToActual()
    },
    copyActualToPlanned({ commit }) {
      commit('SET_LOADING', true)

      return BudgetAPI.copyActualToPlanned()
    },

    async export({ rootState }, { dealerships, format, budget, options = {} }) {
      const { year, month, dealerGroupId } = rootState.settings
      const params = {
        dealerGroupId,
        year,
        month,
        budget,
        format,
        dealerships: dealerships.join(','),
      }
      for (const [key, value] of Object.entries(options)) {
        /* In options[option] you must use 0/1 instead of true/false because of the API features*/
        params[`options[${key}]`] = value ? 1 : 0
      }
      const data = await BudgetAPI.export(params)
      return data
    },
    updateBudget({ commit }, budgetTarget) {
      if (!budgetTarget) {
        return
      }
      commit('SET_STATS', {
        budget: budgetTarget,
      })
      return BudgetAPI.update({ budgetTarget }).then((response) => {
        commit('SET_STATS', response.data)
        return response
      })
    },
  },
  getters: {
    total(state) {
      const { budget, actualBudget, expenses, totalLessCoop, coop } = state.rtb.stats
      return {
        budget,
        actualBudget,
        expenses,
        totalLessCoop,
        coop,
      }
    },
    settingsParams(state, getters, rootState) {
      const { dealerGroupId, dealerShipId, year, viewType, month, quarter } = rootState.settings
      const params = { dealerGroupId, dealerShipId, year }
      switch (viewType) {
        case 'daysOfMonth':
          params.month = month
          break
        case 'monthsOfQuarter':
          params.quarter = quarter
          break
      }
      return params
    },
    budgetType(state) {
      return {
        targetBudget: 'target',
        actualBudget: 'actual',
      }[state.budgetType]
    },
    dealerships(state) {
      return state.rtb.rooftops ?? []
    },
    nullDealerships(state, getters) {
      return getters.dealerships.reduce((acc, item) => {
        acc[item.id] = {
          targetBudget: 0,
          actualBudget: 0,
        }
        return acc
      }, {})
    },
    blankDealerships(state, getters) {
      return [...Array(getters.dealerships?.length).keys()].map(() => [])
    },
    expenseCount(state) {
      const count = {
        categories: 0,
        products: 0,
        campaigns: 0,
      }
      const categories = state.rtb.categories
      const productsCount = (product, title) => {
        const products = product.products || product.subproducts
        if (products) {
          if (product.products) {
            count.products += products.length
          } else if (product.subproducts) {
            count.campaigns += products.length
          }
          for (const item of products) {
            productsCount(item, title)
          }
        }
      }

      count.categories = categories?.length || 0
      if (categories) {
        for (const item of categories) {
          productsCount(item, item.title)
        }
      }
      return count
    },
    categories(state) {
      return (
        state.categoryOrder?.map((id) => {
          const category = find(state.categoriesList || [], {
            productCategoryId: id,
          })
          const denormalizedCategory = denormalize(state.categories[id], CategorySchema, {
            categories: state.categories,
            products: state.products,
            subproducts: state.subproducts,
          })
          return {
            ...category,
            ...denormalizedCategory,
          }
        }) || []
      )
    },
    totalCategories(state, getters) {
      const budgetType = state.budgetType
      const mergeDealershipsWithParent = (dealerships, categoryDealerships = null) =>
        reduce(
          dealerships || {},
          (acc, item, key) => {
            const accPrice = acc[key]?.price || 0
            const dealershipPrice = item.price || item[budgetType] || 0
            const price = accPrice + dealershipPrice
            return {
              ...acc,
              [key]: {
                ...acc[key],
                price,
                targetBudget: (acc[key]?.targetBudget || 0) + (item.targetBudget || 0),
                actualBudget: (acc[key]?.actualBudget || 0) + (item.actualBudget || 0),
                isRowHover: acc[key]?.isRowHover || item.isRowHover,
              },
            }
          },
          { ...categoryDealerships }
        )
      const reduceDealerships = (dealerships) =>
        reduce(
          dealerships,
          (acc, item, key) => ({
            ...acc,
            [key]: {
              ...item,
              price: item[budgetType],
            },
          }),
          {}
        )
      const result = []
      const adaptItems = (parent) => {
        const copy = { ...parent }
        const items = copy.products || copy.subproducts || []
        for (const i of Object.keys(items)) {
          const item = adaptItems(items[i])
          if (item.dealerShips) {
            item.dealerShips = reduceDealerships(item.dealerShips)
            copy.dealerShips = mergeDealershipsWithParent(
              item.dealerShips,
              cloneDeep(copy.dealerShips)
            )
          }
          items[i] = item
        }
        copy.price = copy[budgetType]
        return copy
      }
      for (const category of getters.categories) {
        result.push(adaptItems(cloneDeep(category)))
      }
      return result
    },
  },
}

const moduleMixin = (subModules = []) => {
  let result = { ...budgetModule }
  for (const subModule of subModules) {
    result = merge(result, subModule)
  }
  return result
}

export default moduleMixin(subModules)
